import { ResourceManager } from '@rosetta/resource-manager';
import common from '@/mixin/common';
import layoutID from '@/mixin/layoutID';
import * as constants from '@/mixin/constants';
import * as locales from '@/mixin/locales';
import * as exclusions from '@/mixin/exclusions';

const converter = require('xml-js');

var resourceManager = null;

export default {
  data: function () {
    return {
      language: null,
      scriptSystems: null,
      confuserWordList: null,
      localeId: null,
      localeTexts: null,
    }
  },
  mixins: [
    common,
    layoutID,
  ],
  methods: {
    /**
     * ResourceManagerを初期化する
     *
     * @param {Array} keys 復号キー
     */
    initResourceManager: function (keys) {
      resourceManager = new ResourceManager(keys,
        "https://resources.rosettastone.com/rs3/content/",
        "http://tracking.rosettastone.com/resources/",
        true);
    },
    /**
     * リソースIDを指定して各リソースを取得する
     *
     * @param {String} resourceId リソースID
     * @return {Promise} resolve：各リソースオブジェクト, reject：エラー用オブジェクト
     */
    loadResource: function (resourceId) {
      var self = this;
      return new Promise(function (resolve, reject) {
        resourceManager.loadResource(resourceId).then(function (resource) {
          switch (resource.type) {
            case "text/*":
              var reader = new FileReader();
              reader.addEventListener("load", function () {
                resolve({ loadedResource: this.result, loadedResourceType: 'text' });
              });
              reader.addEventListener("error", function (e) {
                reject({ loadedResource: e, loadedResourceType: 'error' });
              });
              reader.readAsText(resource);
              break;
            case "audio/mpeg":
              resolve({ loadedResource: URL.createObjectURL(resource), loadedResourceType: 'audio' });
              break;
            default:
              resolve({ loadedResource: URL.createObjectURL(resource), loadedResourceType: 'image' });
              break;
          }
        }).catch(function (e) {
          reject({ loadedResource: e, loadedResourceType: 'error' });
        });
      });
    },
    /**
     * リソースIDを指定してXMLをJSONで取得する
     *
     * @param {String} resourceId リソースID
     *
     * @return {Promise} resolve：JSONオブジェクト, reject：エラー文字列
     */
    loadXmlResource: function (resourceId) {
      var self = this;
      return new Promise(function (resolve, reject) {
        self.loadResource(resourceId).then(function (resource) {
          if (resource.loadedResourceType == 'text') {
            resolve(converter.xml2js(resource.loadedResource));
          } else {
            reject("loadedResourceType is not text");
          }
        }).catch(function (e) {
          self.log(e);
          reject("loadXmlResource error");
        });
      });
    },
    /**
     * リソースIDを指定してTSVをJSONで取得する
     *
     * @param {String} resourceId リソースID
     *
     * @return {Promise} resolve：JSONオブジェクト, reject：エラー文字列
     */
    loadTsvResource: function (resourceId) {
      var self = this;
      return new Promise(function (resolve, reject) {
        self.loadResource(resourceId).then(function (resource) {
          if (resource.loadedResourceType == 'text') {
            resolve(self.parseTsv(resource.loadedResource));
          } else {
            reject("loadedResourceType is not text");
          }
        }).catch(function (e) {
          self.log(e);
          reject("loadTsvResource error");
        });
      });
    },
    /**
     * 指定されたTSVをパースする
     *
     * @param {String} tsv TSV
     *
     * @return {Array} パースしたTSV
     */
    parseTsv: function (tsv) {
      var result;

      var list = tsv.replace(/\r\n/g, "\n").split(/\n+/);
      var header;
      for (var i = 0; i < list.length; i++) {
        var line = list[i].split(/\t/);
        if (i == 0) {
          header = line;
          result = [];
        } else if (header.length == line.length) {
          result.push(this.getTsvLine(i, header, line));
        }
      }

      return result;
    },
    /**
     * TSVの行情報を取得する
     *
     * @param {Number} index 行インデックス
     * @param {Array} header ヘッダ情報
     * @param {Array} line 行情報
     *
     * @return {Object} 行情報
     */
    getTsvLine: function (index, header, line) {
      var result = { sortNo: index };
      for (var i = 0; i < header.length; i++) {
        result[header[i]] = line[i];
      }
      return result;
    },
    /**
     * CourseリソースをロードしてJSONで取得する
     *
     * @param {String} resourceId リソースID
     * @param {Number} levelNo レベル番号
     * @param {String} localeId ロケールID
     *
     * @return {Promise} resolve：指定されたCourseリソース～PathリソースまでのJSONオブジェクト, reject：エラー文字列
     */
    loadCourseResource: function (resourceId, levelNo, localeId) {
      var self = this;
      this.localeId = localeId;
      return new Promise(function (resolve, reject) {
        // Courseリソースロード
        self.loadXmlResource(resourceId).then(function (resource) {
          var course = resource.elements.find(function (element) { return element.name == "course"; });
          Promise.all([
            // 選択肢リソースロード
            self.loadConfuserWordListResource(course),
            // Localeリソースロード
            self.loadLocaleResource(course),
          ]).then(async function (resources) {
            // ロードデータを一時保存
            var index = 0;
            self.confuserWordList = resources[index++];
            self.localeTexts = resources[index++];
            // Courseリソースからレベル要素JSONを生成する
            var result = await self.generateLevel(levelNo, course);

            resolve(result);
          }).catch(function (e) {
            self.log(e);
            reject("loadCourseResource error");
          });
        }).catch(function (e) {
          self.log(e);
          reject("loadCourseResource error");
        });
      });
    },
    /**
     * 選択肢リソースをロードしてJSONで取得する
     *
     * @param {Object} course Courseリソース
     *
     * @return {Promise} resolve：JSONオブジェクト, reject：エラー文字列
     */
    loadConfuserWordListResource: function (course) {
      var self = this;
      var confuserWordList = course.elements.find(function (element) { return element.name == "confuserWordList"; });
      var resourceId = confuserWordList.attributes.resource;
      return new Promise(function (resolve, reject) {
        self.loadTsvResource(resourceId).then(function (resource) {
          self.convertConfuserWordList(resource, course.attributes.language);
          resolve(resource);
        }).catch(function (e) {
          self.log(e);
          reject("loadConfuserWordListResource error");
        });
      });
    },
    /**
     * 選択肢リソース変換処理
     *
     * @param {Object} confuserWordList 選択肢リソース
     * @param {String} language 言語コード
     */
    convertConfuserWordList: function (confuserWordList, language) {
      for (var confuserWord of confuserWordList) {
        // 数字を数値に変換
        confuserWord.level = Number(confuserWord.level);
        confuserWord.unit = Number(confuserWord.unit);
        confuserWord.lesson = Number(confuserWord.lesson);
        // ユニット番号を言語/レベル毎に変換
        switch (language) {
          case constants.LANGUAGE_CODE_ZHCV:
            confuserWord.unit = ((confuserWord.level - 1) * 4) + ((confuserWord.unit % 4) + 1);
            break;
          case constants.LANGUAGE_CODE_ENUS:
          case constants.LANGUAGE_CODE_FRFR:
          case constants.LANGUAGE_CODE_ITIT:
          case constants.LANGUAGE_CODE_ESES:
          case constants.LANGUAGE_CODE_DEDE:
          case constants.LANGUAGE_CODE_PTBR:
          case constants.LANGUAGE_CODE_ES419:
          case constants.LANGUAGE_CODE_ENGB:
            switch (confuserWord.level) {
              case 1:
              case 2:
              case 3:
                confuserWord.unit = ((confuserWord.level - 1) * 4) + (confuserWord.unit + 1);
                break;
              default:
                confuserWord.unit++;
                break;
            }
            break;
          default:
            confuserWord.unit++;
            break;
        }
      }
      // ソート(unit:昇順, lesson:昇順)
      confuserWordList.sort(function (a, b) {
        if (a.unit < b.unit) {
          return -1;
        }
        if (a.unit > b.unit) {
          return 1;
        }
        if (a.lesson < b.lesson) {
          return -1;
        }
        if (a.lesson > b.lesson) {
          return 1;
        }
        return a.sortNo - b.sortNo;
      });
    },
    /**
     * LocaleリソースをロードしてJSONで取得する
     *
     * @param {Object} course Courseリソース
     *
     * @return {Promise} resolve：JSONオブジェクト, reject：エラー文字列
     */
    loadLocaleResource: function (course) {
      var self = this;
      var locales = course.elements.find(function (element) { return element.name == "locales"; });
      var target = locales.elements.find(function (element) { return element.attributes.id == self.localeId; });
      var resourceId = target.attributes.resource;
      return new Promise(function (resolve, reject) {
        self.loadXmlResource(resourceId).then(function (resource) {
          var locale = resource.elements.find(function (element) { return element.name == "locale"; });
          resolve(locale.elements);
        }).catch(function (e) {
          self.log(e);
          reject("loadLocaleResource error");
        });
      });
    },
    /**
     * Courseリソースからレベル要素JSONを生成する
     *
     * @param {Number} levelNo レベル番号
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Promise} resolve：レベル要素JSONオブジェクト
     */
    generateLevel: async function (levelNo, source) {
      var result = {};

      var scriptSystems = source.elements.find(function (element) { return element.name == "scriptSystems"; });
      var scriptSystemsDefault = scriptSystems.attributes.default;
      var scriptSystemsConfuserKey = scriptSystems.attributes.confuser_key;
      var scriptSystemsSpeech = scriptSystems.attributes.speech;
      var alphabet = source.elements.find(function (element) { return element.name == "alphabet"; });
      var units = source.elements.filter(function (element) { return element.name == "unit"; });

      result.levelNo = levelNo;
      result.textAlign = source.attributes.textAlign;
      result.scriptSystems = {
        default: scriptSystemsDefault,
        confuserKey: scriptSystemsConfuserKey ? scriptSystemsConfuserKey : scriptSystemsDefault,
        speech: scriptSystemsSpeech ? scriptSystemsSpeech : scriptSystemsDefault,
      };
      result.confuserWordList = this.confuserWordList;
      result.alphabetResourceID = alphabet.attributes.resource;
      result.units = await this.generateUnits(levelNo, units);

      return result;
    },
    /**
     * Courseリソースからユニット要素JSONを生成する
     *
     * @param {Number} levelNo レベル番号
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Promise} resolve：ユニット要素JSONオブジェクト, reject：エラー文字列
     */
    generateUnits: function (levelNo, source) {
      var self = this;
      return new Promise(function (resolve, reject) {
        var result = [];
        var promises = [];
        for (var unit of source) {
          var lessons = unit.elements.filter(function (element) { return element.name == "lesson"; });

          promises.push(self.generateLessons(lessons));

          result.push({
            unitNo: self.getUnitNo(levelNo, unit.attributes.index),
            title: self.getLocaleText(unit.attributes.titleKey),
            lessons: null,
          });
        }
        Promise.all(promises).then(function (resolves) {
          for (var i = 0; i < result.length; i++) {
            result[i].lessons = resolves[i];
          }
          resolve(result);
        }).catch(function (e) {
          self.log(e);
          reject("generateUnits error");
        });
      });
    },
    /**
     * Courseリソースからレッスン要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Promise} resolve：レッスン要素JSONオブジェクト, reject：エラー文字列
     */
    generateLessons: function (source) {
      var self = this;
      return new Promise(function (resolve, reject) {
        var result = [];
        var promises = [];
        for (var lesson of source) {
          var paths = lesson.elements.filter(function (element) { return element.name == "path"; });

          promises.push(self.loadResource(lesson.attributes.thumbnail));

          result.push({
            thumbnail: null,
            paths: self.generatePaths(paths),
          });
        }
        Promise.all(promises).then(function (resources) {
          for (var i = 0; i < result.length; i++) {
            result[i].thumbnail = resources[i].loadedResource;
          }
          resolve(result);
        }).catch(function (e) {
          self.log(e);
          reject("generateLessons error");
        });
      });
    },
    /**
     * CourseリソースからPath要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Array} Path要素JSONオブジェクト
     */
    generatePaths: function (source) {
      var result = [];

      for (var i = 0; i < source.length; i++) {
        var path = source[i];

        // 除外対象のパスを判定
        var isExclusion = exclusions.PATHS.includes(path.attributes.id);
        if (isExclusion) {
          continue;
        }

        result.push({
          sortNo: i,
          type: path.attributes.type,
          title: locales.LOCALE[this.localeId].pathType[path.attributes.type],
          revision: path.attributes.revision,
          resourceID: path.attributes.resource,
          sections: [],
          pathsteps: [],
        });
      }

      // キャリア版以外の場合、表示順でソート
      if (!this.isCarrier()) {
        result.sort(function(a, b) {
          var indexA = constants.PATH_ORDER.indexOf(a.type);
          var indexB = constants.PATH_ORDER.indexOf(b.type);
          if (0 <= indexA && 0 <= indexB) {
            if (indexA < indexB) {
              return -1;
            }
            if (indexA > indexB) {
              return 1;
            }
          }

          return a.sortNo - b.sortNo;
        });
      }

      return result;
    },
    /**
     * ユニット/レッスンでフィルタリングしたConfuserWordListを返す
     *
     * @param {Array} confuserWordList ConfuserWordList
     * @param {Number} unitNo ユニット番号
     * @param {Number} lessonIndex レッスンIndex
     *
     * @return {Array} フィルタリングしたConfuserWordList
     */
    filterConfuserWordList: function (confuserWordList, unitNo, lessonIndex) {
      var result = [];

      var push = Array.prototype.push;

      // 指定ユニット未満のリストを抽出
      push.apply(result, confuserWordList.filter(function (target) {
        return target.unit < unitNo;
      }));

      // 指定レッスン以下のリストを抽出
      var lessonNo = lessonIndex + 1;
      push.apply(result, confuserWordList.filter(function (target) {
        if (unitNo != target.unit) {
          return false;
        }
        if (lessonNo < target.lesson) {
          return false;
        }
        return true;
      }));

      return result;
    },
    /**
     * PathリソースをロードしてJSONで取得する
     *
     * @param {String} resourceId リソースID
     * @param {String} language 言語コード
     * @param {Object} scriptSystems 表示テキスト設定
     * @param {Array} confuserWordList 選択肢生成用リスト
     *
     * @return {Promise} resolve：指定されたPathリソース以下のJSONオブジェクト, reject：エラー文字列
     */
    loadPathResource: function (resourceId, language, scriptSystems, confuserWordList) {
      var self = this;
      this.language = language;
      this.scriptSystems = scriptSystems;
      this.confuserWordList = confuserWordList;
      return new Promise(function (resolve, reject) {
        self.loadXmlResource(resourceId).then(function (resource) {
          var path = resource.elements.find(function (element) { return element.name == "path"; });
          var sections = path.elements.find(function (element) { return element.name == "sections"; });
          var pathsteps = path.elements.filter(function (element) { return element.name == "pathstep"; });
          var result = {
            sections: self.generateSections(sections),
            pathsteps: self.generatePathsteps(pathsteps),
          };
          resolve(result);
        }).catch(function (e) {
          self.log(e);
          reject("loadPathResource error");
        });
      });
    },
    /**
     * PathリソースからSections要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Array} Sections要素JSONオブジェクト
     */
    generateSections: function (source) {
      var result = [];

      // Section要素がない場合
      if (!source.elements) {
        return result;
      }

      var sections = source.elements.filter(function (element) { return element.name == "section"; });
      for (var section of sections) {
        result.push({
          startID: section.attributes.start,
          endID: section.attributes.end,
          scoreThreshold: section.attributes.scoreThreshold,
        });
      }

      return result;
    },
    /**
     * PathリソースからPathstep要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Array} Pathstep要素JSONオブジェクト
     */
    generatePathsteps: function (source) {
      var result = [];

      for (var pathstep of source) {
        // タイピング問題は除外
        if (pathstep.attributes.mode == "typing") {
          continue;
        }

        var acts = this.getActs(pathstep);
        var runMode = this.getRunMode(pathstep);
        var layoutName = this.getLayoutName(pathstep);
        var groupIds = acts.map(function (act) { return act.attributes.groupID; });
        result.push({
          id: pathstep.attributes.id,
          modeGroup: pathstep.attributes.mode,
          mode: runMode,
          layoutID: this.convertLayoutID(runMode, layoutName, groupIds),
          rtlLayout: this.isRtlLayout(layoutName),
          challengeFillPolicy: pathstep.attributes.challengeFillPolicy.charAt(0),
          scramble: pathstep.attributes.scramble == "always",
          actNum: acts.length,
          groupNum: this.getGroupCount(pathstep),
          acts: this.generateActs(acts, runMode),
        });
      }

      return result;
    },
    /**
     * PathリソースからAct要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     * @param {String} runMode RunMode
     *
     * @return {Array} Act要素JSONオブジェクト
     */
    generateActs: function (source, runMode) {
      var result = [];

      for (var act of source) {
        var image = act.elements.find(function (element) { return element.name == "image"; });

        result.push({
          group: act.attributes.groupID,
          usage: this.getUsage(act),
          texts: this.getTexts(runMode, act),
          textPolicy: this.getActPolicy(act, "text").charAt(0),
          sounds: this.getSounds(runMode, act),
          soundPolicy: this.getActPolicy(act, "sound").charAt(0),
          image: image.attributes.resource,
          imageTexts: this.getImageTexts(image),
        });
      }

      return result;
    },
    /**
     * レベル番号とユニットIndexからユニット番号を算出し取得する
     *
     * @param {Number} levelNo レベル番号
     * @param {String} unitIndex ユニットIndex
     *
     * @return {Number} ユニット番号
     */
    getUnitNo: function (levelNo, unitIndex) {
      return Number(unitIndex) + 1 + ((levelNo - 1) * 4);
    },
    /**
     * ロケールテキストリストから指定したキーのテキストを取得する
     *
     * @param {String} key キー
     *
     * @return {String} 指定したキーのテキスト
     */
    getLocaleText: function (key) {
      var locale = this.localeTexts.find(function (element) { return element.attributes.id == key; });
      return locale.attributes.text;
    },
    /**
     * Pathstep要素からAct要素を取得する
     *
     * @param {Object} pathstep Pathstep要素
     *
     * @return {Array} Act要素
     */
    getActs: function (pathstep) {
      var acts = pathstep.elements.find(function (element) { return element.name == "acts"; });
      return acts.elements.filter(function (element) { return element.name == "act"; });
    },
    /**
     * レイアウト名取得
     *
     * @param {Object} pathstep Pathstep要素
     *
     * @return {String} レイアウト名
     */
    getLayoutName: function (pathstep) {
      var layout = pathstep.elements.find(function (element) { return element.name == "layout"; });
      return layout.attributes ? layout.attributes.name : "milestone";
    },
    /**
     * グループ数取得
     *
     * @param {Object} pathstep Pathstep要素
     *
     * @return {Number} グループ数
     */
    getGroupCount: function (pathstep) {
      var groups = pathstep.elements.find(function (element) { return element.name == "groups"; });
      return groups.elements ? groups.elements.filter(function (element) { return element.name == "group"; }).length : 0;
    },
    /**
     * 問題の出題方法を示す値を返す
     *
     * @param {Object} act Act要素
     *
     * @return {Number} 問題の出題方法を示す値
     */
    getUsage: function (act) {
      switch (act.attributes.usage) {
        case "given":
          return 0;
        case "challenge":
          return 1;
        case "context":
          return -1;
        default:
          return null;
      }
    },
    /**
     * Act内の指定された要素のPolicy属性を返す
     *
     * @param {Object} act Act要素
     * @param {String} name 要素名
     *
     * @return {String} Policy属性
     */
    getActPolicy: function (act, name) {
      var target = act.elements.find(function (element) { return element.name == name; });
      return target.attributes.policy;
    },
    /**
     * RunMode取得
     *
     * @param {Object} pathstep Pathstep要素
     *
     * @return {String} RunMode
     */
    getRunMode: function (pathstep) {
      var self = this;
      var mode = pathstep.attributes.mode;
      var challengeFillPolicy = pathstep.attributes.challengeFillPolicy;
      var respondBy = pathstep.attributes.respondBy;
      var scramble = pathstep.attributes.scramble;
      var layoutName = this.getLayoutName(pathstep);
      var groupCount = this.getGroupCount(pathstep);
      var acts = this.getActs(pathstep);
      var isAllNeverProvidedTextPolicy = acts.every(function (act) { return self.getActPolicy(act, "text") == "neverProvided"; });
      var isAnyNeverProvidedTextPolicy = acts.some(function (act) { return self.getActPolicy(act, "text") == "neverProvided"; });
      var isAllNeverProvidedSoundPolicy = acts.every(function (act) { return self.getActPolicy(act, "sound") == "neverProvided"; });
      var isAnyRewardMediaImagePolicy = acts.some(function (act) { return self.getActPolicy(act, "image") == "rewardMedia"; });
      var usageChallenges = acts.filter(function (act) { return act.attributes.usage == "challenge"; });
      var isAnySimultaneous = acts.some(function (act) { return act.attributes.simultaneous == "true"; });

      switch (mode) {
        case "actChoice":
          switch (challengeFillPolicy) {
            case "inSequence":
              return "FAD";
            default:
              switch (respondBy) {
                case "text":
                  switch (scramble) {
                    case "never":
                      return "C3O";
                    case "always":
                      return "C3";
                    default:
                      return null;
                  }
                case "text sound":
                  switch (scramble) {
                    case "never":
                      return "A3O";
                    case "always":
                      return "A3";
                    default:
                      return null;
                  }
                case "text picture":
                  switch (scramble) {
                    case "never":
                      return "A4O";
                    case "always":
                      return "A4";
                    default:
                      return null;
                  }
                case "picture":
                  if (isAllNeverProvidedTextPolicy) {
                    switch (scramble) {
                      case "never":
                        return "B1O";
                      case "always":
                        return "B1";
                      default:
                        return null;
                    }
                  } else if (isAllNeverProvidedSoundPolicy) {
                    switch (scramble) {
                      case "never":
                        return "C1O";
                      case "always":
                        return "C1";
                      default:
                        return null;
                    }
                  } else {
                    switch (scramble) {
                      case "never":
                        return "A1O";
                      case "always":
                        return "A1";
                      default:
                        return null;
                    }
                  }
                default:
                  return null;
              }
          }
        case "cueChoice":
          switch (layoutName) {
            case "3x2-1":
              return "ThreeTwoOne";
            default:
              if (isAnyRewardMediaImagePolicy) {
                if (layoutName == "2x2" && groupCount == 2) {
                  return "TwoTwo";
                } else {
                  return "A3Q";
                }
              } else if (isAllNeverProvidedTextPolicy) {
                return "B1Q";
              } else if (isAllNeverProvidedSoundPolicy) {
                return "C1Q";
              } else {
                return "A1Q";
              }
          }
        case "speech":
          switch (challengeFillPolicy) {
            case "startOfPage":
              switch (respondBy) {
                case "picture":
                  return "PNS";
                case "sound picture":
                  return "PNT";
                case "text sound picture":
                  return "P";
                default:
                  return null;
              }
            case "inSequence":
              if (isAnyNeverProvidedTextPolicy) {
                return "PNTSpeech";
              } else if (usageChallenges.length == 1) {
                return "Speech";
              } else if (usageChallenges.length != 0) {
                return "PSpeech";
              } else {
                return null;
              }
            default:
              return null;
          }
        case "tile":
          switch (respondBy) {
            case "sound picture":
              return "LetterTile";
            case "text sound picture":
              if (isAnySimultaneous) {
                return "STile";
              } else {
                return "Tile";
              }
            default:
              return null;
          }
        case "typing":
          switch (respondBy) {
            case "sound picture":
              return "TypingDictation";
            case "picture":
              return "TypingProduction";
            default:
              return null;
          }
        case "milestone":
          return "Milestone";
        default:
          return null;
      }
    },
    /**
     * テキスト情報取得
     *
     * @param {String} runMode RunMode
     * @param {Object} act Act要素
     *
     * @return {Array} テキスト情報
     */
    getTexts: function (runMode, act) {
      var self = this;
      var result = [];

      var needsTileData = this.needsTileData(act);
      var needsTileSounds = this.needsTileSounds(runMode, act);
      var text = act.elements.find(function (element) { return element.name == "text"; });
      var scripts = text.elements.filter(function (element) { return element.name == "script"; });
      var confuserKey = scripts.find(function (element) { return element.attributes.id == self.scriptSystems.confuserKey; });
      for (var script of scripts) {
        result.push({
          scriptID: script.attributes.id,
          basicText: this.getBasicText(script),
          tileText: needsTileData ? this.getTileText(script, confuserKey) : "",
          confusers: needsTileData ? this.getConfusers(script, needsTileSounds, confuserKey) : [],
          superScripts: this.getBasicSuperScripts(script),
        });
      }

      return result;
    },
    /**
     * テキスト取得
     *
     * @param {Object} script Script要素
     *
     * @return {String} テキスト
     */
    getBasicText: function (script) {
      var result = "";

      var scriptId = script.attributes.id;
      for (var element of script.elements) {
        switch (element.name) {
          case "basic":
            result += this.getEmphasisText(element, scriptId);
            break;
          case "confusers":
            result += this.getCorrectText(element, scriptId);
            break;
          default:
            // Ignore
            break;
        }
      }

      return result;
    },
    /**
     * 強調テキスト取得
     *
     * @param {Object} basic Basic要素
     * @param {String} scriptId スクリプトID
     *
     * @return {String} 強調テキスト
     */
    getEmphasisText: function (basic, scriptId) {
      var result = basic.attributes.text;

      var needsEmphases = false;
      if (basic.elements) {
        var emphases = basic.elements.filter(function (element) { return element.name == "emphasis"; });
        if (0 < emphases.length) {
          needsEmphases = true;

          var index = 0;
          var texts = [];
          var tail;

          for (var emphasis of emphases) {
            var emphasisStart = Number(emphasis.attributes.start);
            var emphasisLength = Number(emphasis.attributes.length);

            if (index < emphasisStart) {
              texts.push(result.substring(index, emphasisStart));
              index = emphasisStart;
            }

            var emphasisText = result.substr(index, emphasisLength);
            switch (emphasis.attributes.type) {
              case "primary":
                texts.push("<font color=\"red\">");
                texts.push(emphasisText);
                texts.push("<\/font>");
                break;
              case "secondary":
                texts.push("<font color=\"blue\">");
                texts.push(emphasisText);
                texts.push("<\/font>");
                break;
              case "unvoiced":
                texts.push("<font color=\"silver\">");
                texts.push(emphasisText);
                texts.push("<\/font>");
                break;
              default:
                texts.push(emphasisText);
                break;
            }

            index += emphasisLength;
            tail = result.substring(index);
          }

          texts.push(tail);

        // 中国語かつピンイン以外の場合は半角スペース除去
        if (this.language == constants.LANGUAGE_CODE_ZHCV && scriptId != constants.TEXT_SCRIPTID_PINYIN) {
          var trimSpace = texts.map(function (text) {
            return text.match(/^\<font.*/) ? text : text.replace(/\s/, '');
          });
          texts = trimSpace;
        }

        result = texts.join('');

        }
      }

      // 中国語かつピンイン以外かつ強調表示無しの場合は半角スペース除去
      if (this.language == constants.LANGUAGE_CODE_ZHCV && scriptId != constants.TEXT_SCRIPTID_PINYIN && !needsEmphases) {
        result = result.replace(/\s/, '');
      }

      return result;
    },
    /**
     * Confusers要素の正解テキスト取得
     *
     * @param {Object} confusers Confusers要素
     * @param {String} scriptId スクリプトID
     *
     * @return {String} Confusers要素の正解テキスト
     */
    getCorrectText: function (confusers, scriptId) {
      var choices = confusers.elements.filter(function (element) { return element.name == "choice"; });
      var correct = choices.find(function (choice) { return choice.attributes.correct == "true"; });
      // 中国語かつピンイン以外の場合は半角スペース除去
      if (this.language == constants.LANGUAGE_CODE_ZHCV && scriptId != constants.TEXT_SCRIPTID_PINYIN) {
        return correct.attributes.text.replace(/\s/, '');
      } else {
        return correct.attributes.text;
      }
    },
    /**
     * Tile用データの要否を返す
     *
     * @param {Object} act Act要素
     *
     * @return {Boolean} true：必要, false：不要
     */
    needsTileData: function (act) {
      if (act.attributes.usage != "challenge") {
        return false;
      }
      switch (act.attributes.mode) {
        case "tile":
          return true;
        case "speech":
          var policy = this.getActPolicy(act, "text");
          return policy == "rewardMedia";
        default:
          return false;
      }
    },
    /**
     * Tile用テキスト取得
     *
     * @param {Object} script Script要素
     * @param {Object} confuserKey 選択肢検索時のキーとなるScript要素
     *
     * @return {String} Tile用テキスト
     */
    getTileText: function (script, confuserKey) {
      var result = "";

      var scriptId = script.attributes.id;
      var index = 0;
      if (this.existsConfusers(script)) {
        // Script要素内にConfusers要素が存在する場合、Confusers要素からテキストを生成
        for (var element of script.elements) {
          switch (element.name) {
            case "basic":
              // 中国語かつピンイン以外の場合は半角スペース除去
              if (this.language == constants.LANGUAGE_CODE_ZHCV && scriptId != constants.TEXT_SCRIPTID_PINYIN) {
                result += element.attributes.text.replace(/\s/, "");
              } else {
                result += element.attributes.text;
              }
              break;
            case "confusers":
              result += "[" + String(index++) + "]";
              break;
            default:
              // Ignore
              break;
          }
        }
      } else {
        // Script要素内にConfusers要素が存在しない場合、ConfuserWordListからテキストを生成
        var wordList = this.getWordList(script);
        var confuserKeyList = this.getConfuserKeyList(confuserKey, scriptId);
        var checkList = this.getTileCheckList(confuserKeyList);
        var existsTile = checkList.includes(true);
        if (existsTile) {
          var texts = [];
          for (var i = 0; i < wordList.length; i++) {
            var concatenate;
            if (checkList[i]) {
              // 選択肢対象の場合、Tile用文字列に変換
              concatenate = "[" + String(index++) + "]";
              // 中国語かつピンインで末尾の文字が特定の文字だった場合、Tile用文字列と分ける
              if (this.language == constants.LANGUAGE_CODE_ZHCV && scriptId == constants.TEXT_SCRIPTID_PINYIN && wordList[i].match(/^.*[\,|\.|\?]$/)) {
                concatenate += wordList[i].slice(-1);
              }
            } else {
              // 選択肢対象外の文字列
              concatenate = wordList[i];
            }
            texts.push(concatenate);
          }
          switch (this.language) {
            case constants.LANGUAGE_CODE_ZHCV:
              // ピンインの場合は半角スペース区切りで結合する
              // 簡体字または繁体字は区切り文字無しで結合する
              result = texts.join(scriptId == constants.TEXT_SCRIPTID_PINYIN ? " " : "");
              // ピンイン以外の場合は半角スペース除去
              if (scriptId != constants.TEXT_SCRIPTID_PINYIN) {
                result = result.replace(/\s/, "");
              }
              break;
            case constants.LANGUAGE_CODE_JAJP:
              // ローマ字の場合は半角スペース区切りで結合する
              // ローマ字以外はゼロ幅スペース区切りで結合する
              result = texts.join(scriptId == constants.TEXT_SCRIPTID_ROMAJI ? " " : "\u200b");
              break;
            default:
              // 上記以外は半角スペース区切りで結合する
              result = texts.join(" ");
              break;
          }
        }
      }

      return result;
    },
    /**
     * 選択肢情報取得
     *
     * @param {Object} script Script要素
     * @param {Boolean} needsTileSounds Tile用サウンド要否
     * @param {Object} confuserKey 選択肢検索時のキーとなるScript要素
     *
     * @return {Array} 選択肢情報
     */
    getConfusers: function (script, needsTileSounds, confuserKey) {
      var self = this;
      var result = [];

      if (this.existsConfusers(script)) {
        // Script要素内にConfusers要素が存在する場合、Confusers要素から選択肢情報を生成
        var confusers = script.elements.filter(function (element) { return element.name == "confusers"; });
        for (var confuser of confusers) {
          var choices = confuser.elements.filter(function (element) { return element.name == "choice"; });
          result.push(choices.map(function (choice) {
            return {
              text: choice.attributes.text,
              sound: needsTileSounds ? choice.attributes.soundResource : null,
              correct: choice.attributes.correct == "true",
              superScripts: self.getSuperScripts(choice),
            };
          }));
        }
      } else {
        // Script要素内にConfusers要素が存在しない場合、ConfuserWordListから選択肢情報を生成
        var scriptId = script.attributes.id;
        var wordList = this.getWordList(script);
        var confuserKeyList = this.getConfuserKeyList(confuserKey, scriptId);
        var checkList = this.getTileCheckList(confuserKeyList);
        for (var i = 0; i < wordList.length; i++) {
          if (!checkList[i]) {
            continue;
          }
          var choices = [{
            text: wordList[i],
            sound: null,
            correct: true,
            superScripts: this.getCorrectSuperScripts(script, wordList[i]),
          }];
          var confuserWordList = this.getConfuserWordList(confuserKeyList[i]);
          for (var confuserWord of confuserWordList) {
            choices.push({
              text: this.getConfuserWord(confuserWord, scriptId),
              sound: null,
              correct: false,
              superScripts: this.getConfuserSuperScripts(confuserWord, scriptId),
            });
            // 選択肢は最大で4つ
            if (3 < choices.length) {
              break;
            }
          }
          result.push(choices);
        }
      }

      return result;
    },
    /**
     * Script要素内にConfusers要素が存在するか否かを返す
     *
     * @param {Object} script Script要素
     *
     * @return {Boolean} true：Confusers要素が存在する, false：Confusers要素が存在しない
     */
    existsConfusers: function (script) {
      return script.elements.some(function (element) { return element.name == "confusers"; });
    },
    /**
     * 正解テキストから単語リストを取得する
     *
     * @param {Object} script Script要素
     *
     * @return {Array} 正解テキストの単語リスト
     */
    getWordList: function (script) {
      var result;

      var basic = script.elements.find(function (element) { return element.name == "basic"; });
      switch (this.language) {
        case constants.LANGUAGE_CODE_ZHCV:
          if (script.attributes.id == constants.TEXT_SCRIPTID_PINYIN) {
            // 中国語かつピンインの場合、半角スペース区切りでリストを生成する
            result = basic.attributes.text.split(/[\u0020]/);
          } else {
            // 中国語かつピンイン以外の場合、1文字ずつのリストを生成する
            result = basic.attributes.text.split("");
          }
          break;
        default:
          // 上記以外の場合、「半角スペース or ゼロ幅スペース」区切りで単語リストを生成する
          result = basic.attributes.text.split(/[\u0020]|[\u200b]/);
          break;
      }

      return result;
    },
    /**
     * 選択肢生成のキーとなる単語リストを取得する
     *
     * @param {Object} confuserKey 選択肢検索時のキーとなるScript要素
     * @param {String} scriptId スクリプトID
     *
     * @return {Array} 選択肢生成のキーとなる単語リスト
     */
    getConfuserKeyList: function (confuserKey, scriptId) {
      var result;

      if (this.language == constants.LANGUAGE_CODE_ZHCV && scriptId == constants.TEXT_SCRIPTID_PINYIN) {
        // 中国語かつピンインの場合、特定の文字を除去したテキストからリストを生成する
        var basic = confuserKey.elements.find(function (element) { return element.name == "basic"; });
        const replaceWords = ["？", "，", "。", "、", "!", "”", "“", "！", ".", "：", " ", "　", "\uFEFF"];
        var confuserKeyText = basic.attributes.text;
        for (var replaceWord of replaceWords) {
          confuserKeyText = confuserKeyText.replace(replaceWord, "");
        }
        result = confuserKeyText.split("");
      } else {
        // 上記以外の場合、指定されたScript要素から単語リストを取得する
        result = this.getWordList(confuserKey);
      }

      return result;
    },
    /**
     * Tile表示する単語か否かのリストを返す
     *
     * @param {Array} wordList 単語リスト
     *
     * @return {Array} Tile表示する単語か否かのリスト
     */
    getTileCheckList: function (wordList) {
      var result = [];

      for (var word of wordList) {
        // ConfuserWordListリストを検索
        var confuserWordList = this.getConfuserWordList(word);
        // 検索でヒットすれば、Tile表示対象
        result.push(0 < confuserWordList.length);
      }

      return result;
    },
    /**
     * 指定した単語に紐付く選択肢リストを取得する
     *
     * @param {String} word 単語
     *
     * @return {Array} 指定した単語に紐付く選択肢リスト
     */
    getConfuserWordList: function (word) {
      return this.confuserWordList.filter(function (element) {
        var target = element.word ? element.word : element["word:text"];
        return word == target;
      });
    },
    /**
     * 指定した選択肢情報から指定したスクリプトIDの単語を取得する
     *
     * @param {Object} confuserWord 選択肢情報
     * @param {String} scriptId スクリプトID
     *
     * @return {String} 対象の単語
     */
    getConfuserWord: function (confuserWord, scriptId) {
      var result = confuserWord.confuser;
      if (result) {
        return result;
      }
      result = confuserWord["confuser:" + scriptId + ":text"];
      if (result) {
        return result;
      }
      result = confuserWord["confuser:" + scriptId + ":xml"];
      if (result) {
        var xml = converter.xml2js(result);
        var basic = xml.elements.find(function (element) { return element.name == "basic"; });
        return basic.attributes.text;
      }
      return result;
    },
    /**
     * サウンドリソースID取得
     *
     * @param {String} runMode RunMode
     * @param {Object} act Act要素
     *
     * @return {Array} サウンドリソースID
     */
    getSounds: function (runMode, act) {
      var result = this.needsTileSounds(runMode, act) ? this.getTileSounds(act) : [];
      var sound = act.elements.find(function (element) { return element.name == "sound"; });
      result.push(sound.attributes.resource);
      return result;
    },
    /**
     * Tile用サウンドの要否を返す
     *
     * @param {String} runMode RunMode
     * @param {Object} act Act要素
     *
     * @return {Boolean} true：必要, false：不要
     */
    needsTileSounds: function (runMode, act) {
      switch (runMode) {
        case "Tile":
        case "STile":
          return act.attributes.usage == "challenge";
        default:
          return false;
      }
    },
    /**
     * TileモードのサウンドリソースID取得
     *
     * @param {Object} act Act要素
     *
     * @return {Array} サウンドリソースID
     */
    getTileSounds: function (act) {
      var result = [];

      var text = act.elements.find(function (element) { return element.name == "text"; });
      var script = text.elements.find(function (element) { return element.name == "script"; });
      for (var element of script.elements) {
        switch (element.name) {
          case "basic":
            result.push(element.attributes.soundResource);
            break;
          case "confusers":
            result.push("static/resource/sound/tileSound.mp3");
            break;
          default:
            // Ignore
            break;
        }
      }

      return result;
    },
    /**
     * 画像に付随する文字情報を取得
     *
     * @param {Object} image Image要素
     *
     * @return {Array} 画像に付随する文字情報
     */
    getImageTexts: function (image) {
      var result = [];

      if (image.elements) {
        var text = image.elements.find(function (element) { return element.name == "text"; });
        var scripts = text.elements.filter(function (element) { return element.name == "script"; });
        for (var script of scripts) {
          var imageText = script.attributes.string;
          switch (this.language) {
            case constants.LANGUAGE_CODE_NLNL:
            case constants.LANGUAGE_CODE_ELGR:
            case constants.LANGUAGE_CODE_SVSE:
            case constants.LANGUAGE_CODE_HIIN:
            case constants.LANGUAGE_CODE_PLPL:
            case constants.LANGUAGE_CODE_JAJP:
              imageText = imageText.replace(/\s/g, "");
              break;
            default:
              // Ignore
              break;
          }
          result.push({
            scriptID: script.attributes.id,
            text: imageText,
          });
        }
      }

      return result;
    },
    /**
     * 問題文のふりがな情報を取得する
     *
     * @param {Object} script Script要素
     *
     * @return {Array} ふりがな情報リスト
     */
    getBasicSuperScripts: function (script) {
      var result = [];
      var push = Array.prototype.push;

      var text = "";
      var target;
      for (var scriptElement of script.elements) {
        switch (scriptElement.name) {
          case "basic":
            target = scriptElement;
            break;
          case "confusers":
            var choices = scriptElement.elements.filter(function (element) { return element.name == "choice"; });
            target = choices.find(function (choice) { return choice.attributes.correct == "true"; });
            break;
          default:
            continue;
        }

        var start = text.length;
        text += target.attributes.text;
        push.apply(result, this.getSuperScripts(target, start));
      }

      return result;
    },
    /**
     * 正解の単語のふりがな情報を取得する
     *
     * @param {Object} script Script要素
     * @param {String} correct 正解の単語
     *
     * @return {Array} ふりがな情報リスト
     */
    getCorrectSuperScripts: function (script, correct) {
      var result = [];

      var basic = script.elements.find(function (element) { return element.name == "basic"; });
      var superScripts = this.getSuperScripts(basic);
      var text = basic.attributes.text;
      var start = text.indexOf(correct);
      var end = start + correct.length;
      var targets = superScripts.filter(function (superScript) {
        return start <= superScript.position && superScript.position < end;
      });
      var correction;
      for (var i = 0; i < targets.length; i++) {
        var target = targets[i];
        if (i == 0) {
          correction = target.position;
        }
        result.push({
          position: target.position - correction,
          length: target.length,
          text: target.text,
        });
      }

      return result;
    },
    /**
     * 指定した選択肢情報から指定したスクリプトIDのふりがな情報を取得する
     *
     * @param {Object} confuserWord 選択肢情報
     * @param {String} scriptId スクリプトID
     *
     * @return {Array} ふりがな情報リスト
     */
    getConfuserSuperScripts: function (confuserWord, scriptId) {
      var result = [];

      var confuser = confuserWord["confuser:" + scriptId + ":xml"];
      if (confuser) {
        var xml = converter.xml2js(confuser);
        var basic = xml.elements.find(function (element) { return element.name == "basic"; });
        result = this.getSuperScripts(basic);
      }

      return result;
    },
    /**
     * 指定された要素内のふりがな情報を取得する
     *
     * @param {Object} target 対象要素
     * @param {Number} start 開始位置(未指定可)
     *
     * @return {Array} ふりがな情報リスト
     */
    getSuperScripts: function (target, start) {
      var result = [];

      if (target.elements) {
        var additional = start ? start : 0;
        var superScripts = target.elements.filter(function (element) { return element.name == "superscript"; });
        result = superScripts.map(function (superScript) {
          return {
            position: Number(superScript.attributes.position) + additional,
            length: Number(superScript.attributes.length),
            text: superScript.attributes.text,
          };
        });
      }

      return result;
    },
    /**
     * AlphabetリソースをロードしてJSONで取得する
     *
     * @param {String} resourceId リソースID
     *
     * @return {Promise} resolve：指定されたAlphabetリソースのJSONオブジェクト, reject：エラー文字列
     */
    loadAlphabetResource: function (resourceId) {
      var self = this;
      return new Promise(function (resolve, reject) {
        self.loadXmlResource(resourceId).then(function (resource) {
          var alphabet = resource.elements.find(function (element) { return element.name == "alphabet"; });
          var characters = alphabet.elements.filter(function (element) { return element.name == "character"; });
          var result = { characters: self.generateCharacters(characters) };
          resolve(result);
        }).catch(function (e) {
          self.log(e);
          reject("loadAlphabetResource error");
        });
      });
    },
    /**
     * AlphabetリソースからCharacter要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Promise} resolve：Character要素JSONオブジェクト, reject：エラー文字列
     */
    generateCharacters: function (source) {
      var result = [];

      for (var character of source) {
        var text = character.elements.find(function (element) { return element.name == "text"; });
        var script = text.elements.find(function (element) { return element.name == "script"; });
        var scripts = text.elements.filter(function (element) { return element.name == "script"; });
        var phoneticGroups = character.elements.filter(function (element) { return element.name == "phoneticGroup"; });

        result.push({
          scriptID: script.attributes.id,
          text: this.getCharacterText(scripts),
          sound: character.attributes.resource,
          phoneticGroups: this.generatePhoneticGroups(phoneticGroups),
        });
      }

      return result;
    },
    /**
     * Character要素のtextを取得する
     * 
     * @param {Object} source 生成元オブジェクト
     * @return {Array} 結合したテキスト
     */
    getCharacterText: function (source) {
      var text = "";
      
      for (var script of source) {
          text += " " + script.elements.find(function (element) { return element.type == "text"; }).text;
      }
      
      return text;
    },
    /**
     * AlphabetリソースからPhoneticGroup要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Promise} resolve：PhoneticGroup要素JSONオブジェクト, reject：エラー文字列
     */
    generatePhoneticGroups: function (source) {
      var result = [];

      for (var phoneticGroup of source) {
        var words = phoneticGroup.elements.filter(function (element) { return element.name == "word"; });
        result.push(this.generateWords(words));
      }

      return result;
    },
    /**
     * AlphabetリソースからWord要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Promise} resolve：Word要素JSONオブジェクト, reject：エラー文字列
     */
    generateWords: function (source) {
      var result = [];

      for (var word of source) {
        var scripts = word.elements.filter(function (element) { return element.name == "script"; });

        result.push({
          sound: word.attributes.resource,
          scripts: this.generateScripts(scripts),
        });
      }

      return result;
    },
    /**
     * AlphabetリソースからScript要素JSONを生成する
     *
     * @param {Object} source 生成元オブジェクト
     *
     * @return {Array} Script要素JSONオブジェクト
     */
    generateScripts: function (source) {
      var result = [];

      for (var script of source) {
        var text = script.elements.find(function (element) { return element.name == "text"; });
        var styles = script.elements.filter(function (element) { return element.name == "style"; });
        result.push({
          scriptID: script.attributes.id,
          text: text.attributes.value,
          color: this.getColor(styles),
          underline: this.getUnderline(styles),
        });
      }

      return result;
    },
    /**
     * Style要素のColor設定を取得する
     *
     * @param {Array} styles Style要素配列
     *
     * @return {Object} Color設定
     */
    getColor: function (styles) {
      var color = styles.find(function (style) { return style.attributes.type == "color"; });
      return color ? {
        value: color.attributes.color,
        startIndex: Number(color.attributes.start),
        length: Number(color.attributes.length),
      } : null;
    },
    /**
     * Style要素のUnderline設定を取得する
     *
     * @param {Array} styles Style要素配列
     *
     * @return {Object} Underline設定
     */
    getUnderline: function (styles) {
      var underline = styles.find(function (style) { return style.attributes.type == "underline"; });
      return underline ? {
        startIndex: Number(underline.attributes.start),
        length: Number(underline.attributes.length),
      } : null;
    },
    /**
     * 指定されたPathstep要素内のリソースをロードしてPathstep要素内のリソースIDをBlobURLに更新する
     *
     * @param {Object} pathstep Pathstep要素
     */
    updatePathstepResources: function (pathstep) {
      try {
        for (var act of pathstep.acts) {
          this.updateConfusersResource(act);
          this.updateSoundsResource(act);
          this.updateImageResource(act);
        }
      } catch (e) {
        this.log(e);
        throw new Error("updatePathstepResources error");
      }
    },
    /**
     * 指定されたAct要素のConfusers内のリソースをロードしてConfusers内のリソースIDをBlobURLに更新する
     *
     * @param {Object} act Act要素
     */
    updateConfusersResource: function (act) {
      var self = this;
      var targets = [];
      var promises = [];
      for (var text of act.texts) {
        for (var choices of text.confusers) {
          for (var choice of choices) {
            if (this.isUpdatableResource(choice.sound)) {
              targets.push(choice);
            }
          }
        }
      }
      for (var target of targets) {
        promises.push(this.loadResource(target.sound));
      }
      Promise.all(promises).then(function (resources) {
        for (var i = 0; i < targets.length; i++) {
          targets[i].sound = resources[i].loadedResource;
        }
      }).catch(function (e) {
        self.log(e);
        throw new Error("updateConfusersResource error");
      });
    },
    /**
     * 指定されたAct要素のSoundsのリソースをロードしてリソースIDをBlobURLに更新する
     *
     * @param {Object} act Act要素
     */
    updateSoundsResource: function (act) {
      var self = this;
      var promises = [];
      for (var sound of act.sounds) {
        if (this.isUpdatableResource(sound)) {
          promises.push(this.loadResource(sound));
        }
      }
      Promise.all(promises).then(function (resources) {
        var index = 0;
        for (var i = 0; i < act.sounds.length; i++) {
          if (self.isUpdatableResource(act.sounds[i])) {
            act.sounds[i] = resources[index++].loadedResource;
          }
        }
      }).catch(function (e) {
        self.log(e);
        throw new Error("updateSoundsResource error");
      });
    },
    /**
     * 指定されたAct要素のImageのリソースをロードしてリソースIDをBlobURLに更新する
     *
     * @param {Object} act Act要素
     */
    updateImageResource: function (act) {
      var self = this;
      if (this.isUpdatableResource(act.image)) {
        this.loadResource(act.image).then(function (resource) {
          act.image = resource.loadedResource;
        }).catch(function (e) {
          self.log(e);
          throw new Error("updateImageResource error");
        });
      }
    },
    /**
     * 指定されたCharacter要素内のリソースをロードしてCharacter要素内のリソースIDをBlobURLに更新する
     *
     * @param {Object} character Character要素
     *
     * @return {Promise} resolve：なし, reject：エラー文字列
     */
    updateCharacterResources: function (character) {
      var self = this;
      return new Promise(function (resolve, reject) {
        var promises = [];

        promises.push(self.updateCharacterSoundResource(character));
        for (var words of character.phoneticGroups) {
          promises.push(self.updateWordsResource(words));
        }

        Promise.all(promises).then(function () {
          resolve();
        }).catch(function (e) {
          self.log(e);
          reject("updateCharacterResources error");
        });
      });
    },
    /**
     * 指定されたCharacter要素のSoundのリソースをロードしてリソースIDをBlobURLに更新する
     *
     * @param {Object} character Character要素
     *
     * @return {Promise} resolve：なし, reject：エラー文字列
     */
    updateCharacterSoundResource: function (character) {
      var self = this;
      return new Promise(function (resolve, reject) {
        if (self.isUpdatableResource(character.sound)) {
          self.loadResource(character.sound).then(function (resource) {
            character.sound = resource.loadedResource;
            resolve();
          }).catch(function (e) {
            self.log(e);
            reject("updateCharacterSoundResource error");
          });
        } else {
          resolve();
        }
      });
    },
    /**
     * 指定されたWords要素内のリソースをロードしてWords要素内のリソースIDをBlobURLに更新する
     *
     * @param {Object} words Words要素
     *
     * @return {Promise} resolve：なし, reject：エラー文字列
     */
    updateWordsResource: function (words) {
      var self = this;
      return new Promise(function (resolve, reject) {
        var promises = [];

        for (var word of words) {
          if (self.isUpdatableResource(word.sound)) {
            promises.push(self.loadResource(word.sound));
          }
        }

        Promise.all(promises).then(function (resources) {
          var index = 0;
          for (var word of words) {
            if (self.isUpdatableResource(word.sound)) {
              word.sound = resources[index++].loadedResource;
            }
          }
          resolve();
        }).catch(function (e) {
          self.log(e);
          reject("updatePhoneticGroupsResource error");
        });
      });
    },
    /**
     * 更新可能なリソースIDか否かを返す
     *
     * @param {String} resourceId リソースID
     *
     * @return {Boolean} true：更新可能, false：更新不可
     */
    isUpdatableResource: function (resourceId) {
      return resourceId && !resourceId.match(/^blob.*/) && !resourceId.match(/^.*tileSound\.mp3$/);
    },
    /**
     * 右から順に出題するレイアウトか否かを判断する
     *
     * @param {String} layoutName レイアウト名
     *
     * @return {Boolean} true: 右から出題, false: 左から出題
     */
    isRtlLayout: function (layoutName) {
      // layoutNameに"-rtl"が含まれる場合はtrue,それ以外はfalse
      if (layoutName.match("-rtl")) {
        return true;
      }
      return false;
    }
  },
};



// WEBPACK FOOTER //
// ./src/mixin/resource.js